<script setup>
import {computed, provide, reactive, ref} from "vue";
import useNickname, {USE_NICKNAME_KEY} from "./NicknameComposition";
import AnswerContainerFreetext from "./AnswerContainerFreetext.vue";
import AnswerContainerChoose from "./AnswerContainerChoose.vue";
import AnswerContainerReason from "./AnswerContainerReason.vue";
import VoiceThemeInformationContainer from "./VoiceThemeInformationContainer.vue";
import NicknameContainer from "./NicknameContainer.vue";
const siteUrl = document.querySelector('meta[name="site-url"]').content;
const props = defineProps({
  isShow: {
    type: Boolean,
    required: true,
  },
  currentModal: {
    type: String,
    required: true,
  },
  user: {
    type: Object,
    required: true,
  },
  errors: {
    type: Object,
    required: true,
  },
  voiceTheme: {
    type: Object,
    required: true,
  }
});
const emits = defineEmits(['request-close', 'set-submit']);
const nicknameComposition = useNickname(siteUrl);
provide(USE_NICKNAME_KEY, nicknameComposition);

// 回答
const answer = reactive({
  answer: '',
  voice_theme_choice_ids: [],
});

// 回答方法が選択・記述式の場合のみ発生する理由の編集モード
const isReasonEditMode = ref(false);

// スクロール対象
const scrollTarget = ref(null);

// 記述回答の文字数を正確に数える
// https://qiita.com/ka215/items/d059a78e29adef3978b5#6-%E3%83%9E%E3%83%AB%E3%83%81%E3%83%90%E3%82%A4%E3%83%88%E6%96%87%E5%AD%97%E3%81%AB%E5%AF%BE%E5%BF%9C%E3%81%97%E3%81%9F%E6%96%87%E5%AD%97%E5%88%97%E5%87%A6%E7%90%86
const answerLength = computed(() => {

  //指定された範囲にサロゲートペアが存在するかを boolean で返す
  //upper は 1 バイト目、lower に 2 バイト目を指定する
  //UTF の場合、通常は 2 バイトで一文字だが、サロゲートペアの場合は 4 バイトで一文字を表す
  const isSurrogatePair = (upper, lower) => 0xD800 <= upper && upper <= 0xDBFF && 0xDC00 <= lower && lower <= 0xDFFF;

  //最終的に返す文字数
  let result = 0;

  //サロゲートペアがあればそれを 2 文字ではなく 1 文字としてカウントしつつ文字数を返す
  for (let i = 0; i < answer.answer.length; i++,result++) {
    //サロゲートペアでない文字は一文字とカウントする
    if ( ! isSurrogatePair(answer.answer.charCodeAt(i), answer.answer.length > (i + 1) ? answer.answer.charCodeAt(i + 1) : 0)) {
      continue;
    }

    //サロゲートペアなら一文字ぶん多くカウントする
    i++;
  }

  //スペースと改行を文字として除外する
  for (let i = 0; i < answer.answer.length; i++) {
    //スペース or 改行でなければ何もしない
    if ( ! /\r\n|\r|\n| |　/.test(answer.answer.charAt(i))) {
      continue;
    }

    //文字カウントを減らす
    result--;
  }

  //結果文字列の数を返す
  return result;
});

// 回答の文字数が 1 文字以上 1000 文字以内かどうか
const isAnswerLengthValid = computed(() => answerLength.value > 0 && answerLength.value <= 1000);

// 次へボタンの状態
const nextStatus = computed(() => {
  // 自由記述の場合
  if (props.currentModal === 'free') {
    // ・ニックネームが選択済
    // ・回答が 1 文字以上 1000 文字以内
    // この条件を満たすとき、次へボタンを有効化
    return Number(nicknameComposition.state.nicknameId) > -2 && isAnswerLengthValid.value
      ? 'submit'
      : 'disabled';
  }

  // 選択式(複数選択不可)の場合
  if (props.currentModal === 'choose' && Number(props.voiceTheme.value.enable_multiple_choice) === 0) {
    // ・ニックネームが選択済
    // ・選択肢が 1 つだけ
    // この条件を満たすとき、次へボタンを有効化
    return Number(nicknameComposition.state.nicknameId) > -2 && answer.voice_theme_choice_ids.length === 1
      ? 'submit'
      : 'disabled';
  }

  // 選択式(複数選択可)の場合
  if (props.currentModal === 'choose' && Number(props.voiceTheme.value.enable_multiple_choice) === 1) {
    // ・ニックネームが選択済
    // ・選択肢が 1 つ以上
    // この条件を満たすとき、次へボタンを有効化
    return Number(nicknameComposition.state.nicknameId) > -2 && answer.voice_theme_choice_ids.length > 0
      ? 'submit'
      : 'disabled';
  }

  // 以下から選択・記述式の場合、ニックネームが選択されていない
  if (Number(nicknameComposition.state.nicknameId) < -1) {
    return 'disabled';
  }

  // 選択肢が 1 つだけ選択されていない
  if (answer.voice_theme_choice_ids.length !== 1) {
    return 'disabled';
  }

  // 選択された回答の情報を得る
  const choice = props.voiceTheme.value.voice_theme_choices.find(choice => Number(choice.id) === Number(answer.voice_theme_choice_ids[0]));

  // 選択された回答が存在しない
  if ( ! choice) {
    return 'disabled';
  }

  // 選択された回答が理由を入力できない
  if (Number(choice.is_exist_reason) === 0) {
    return 'submit';
  }

  // 現在理由編集モードではない
  if ( ! isReasonEditMode.value) {
    return 'reason';
  }

  // 選択された回答が理由を必須としているかつ、文字数条件をクリアしていない
  return Number(choice.is_required_reason) !== 0 && ! isAnswerLengthValid.value
    ? 'disabled'
    : 'submit';
});

// 回答が一つだけ選ばれている場合、選択された回答の情報を得る
const choice = computed(() => {
  // 選択された回答数が 1 以外の場合は null
  if (answer.voice_theme_choice_ids.length !== 1) {
    return null;
  }

  // 選択された回答の情報を返す
  return props.voiceTheme.value.voice_theme_choices.find(choice => Number(choice.id) === Number(answer.voice_theme_choice_ids[0]));
});

// 複数選択可能かどうか
const isMultipleChoice = computed(() => props.currentModal === 'choose' && Number(props.voiceTheme.value.enable_multiple_choice) !== 0);

// 選択肢の選択状態を切り替える
const toggleChoices = (payload) => {
  // 複数選択可能な場合(チェックボックスの挙動)
  if (isMultipleChoice.value) {
    // answer.voice_theme_choice_ids に payload.id が含まれているかどうか
    const index = answer.voice_theme_choice_ids.indexOf(payload.id);

    // payload.checked が true かつ answer.voice_theme_choice_ids に payload.id が含まれていない場合は追加
    if (payload.checked && index === -1) {
      answer.voice_theme_choice_ids.push(payload.id);
    }

    // payload.checked が false かつ answer.voice_theme_choice_ids に payload.id が含まれている場合は削除
    if ( ! payload.checked && index !== -1) {
      answer.voice_theme_choice_ids.splice(index, 1);
    }
  }

  // 複数選択不可の場合(ラジオボタンの挙動)
  else {
    // 一旦全ての選択を解除
    answer.voice_theme_choice_ids = [];

    // 解除でなく選択操作であれば追加
    if (payload.checked) {
      answer.voice_theme_choice_ids.push(payload.id);
    }
  }
};

// 次へボタンのアクション
const nextAction = () => {
  // 次のステータスによって処理を分岐
  switch (nextStatus.value) {
    // 理由回答画面に進む
    case 'reason':
      isReasonEditMode.value = true;
      break;

    // サーバに回答を依頼する
    case 'submit':
      emits('set-submit', {nicknameId: nicknameComposition.state.nicknameId, answer:  answer.answer, choiceIds: answer.voice_theme_choice_ids});
      break;
  }

  // スクロール位置をトップに戻す
  if (scrollTarget.value) {
    scrollTarget.value.scrollTop = 0;
  }
};
</script>

<template>
  <div v-if="isShow">
    <div :class="{modModalWindow: true, isShow}">
      <div class="modModalTitleContainer">
        <p class="lyInlineBlock lyPt6 lyPb6 lyPr8 lyPl8 pFont12 pFontBold pFontWhite pBackColorBlue pBorderRadiusEllipse">回答受付中</p>
        <button
          type="button"
          class="lyHvCnt lyColumn lyW48px lyH48px pFont10 pFontBold"
          @click="emits('request-close')"
        ><span class="lyBlock lyW24px"><img
          :src="siteUrl+'images/iconClose.svg'"
          loading="lazy"
          alt="" width="30"
          height="31"
        ></span>
          とじる
        </button>
      </div>
      <div
        ref="scrollTarget"
        class="modModalBottomWrapper"
      >
        <VoiceThemeInformationContainer :voice-theme="voiceTheme" />
        <div class="lyPt16 lyPb16 lyPl8 lyPr8 lyTbPl16 lyTbPr16">
          <div
            v-if="currentModal !== 'select' || ! isReasonEditMode"
            class="lyMb24">
            <div class="lyHorizontalBreak lyMb8">
              <p class="lyMr8 pFont12">回答者名</p>
            </div>
            <NicknameContainer />
          </div>
          <AnswerContainerFreetext
            v-if="currentModal === 'free'"
            :answer="answer.answer"
            :answer-length="answerLength"
            :is-answer-valid="isAnswerLengthValid"
            :question-title="voiceTheme.value.question_title"
            :errors="errors"
            @set-answer="answer.answer = $event"
          />
          <AnswerContainerChoose
            v-if="currentModal === 'choose' || (currentModal === 'select' && ! isReasonEditMode)"
            :choices="voiceTheme.value.voice_theme_choices"
            :choice-ids="answer.voice_theme_choice_ids"
            :question-title="voiceTheme.value.question_title"
            :is-multiple-choice="isMultipleChoice"
            :errors="errors"
            @toggle-choice="toggleChoices"
          />
          <AnswerContainerReason
            v-if="currentModal === 'select' && isReasonEditMode"
            :answer="answer.answer"
            :answer-length="answerLength"
            :is-answer-valid="isAnswerLengthValid"
            :choice="choice"
            :errors="errors"
            @set-answer="answer.answer = $event"
          />
          <button
            :class="{lyHvCnt: true, lyW100: true, lyPt12: true, lyPb12: true, lyTextCnt: true, pFont14: true, pFontBold: true, pFontWhite: true, pBackColorGreen: true, pBorderRadius2px: true, pHoverSlideChange: true, isDisabled: nextStatus === 'disabled'}"
            type="button"
            @click="nextAction"
          >{{nextStatus === 'submit' ? '回答する' : '次へ'}}</button>
          <button
            v-if="isReasonEditMode"
            class="lyHvCnt lyW100 lyPt12 lyPb12 lyTextCnt pFont14 pFontBold pFontGreen pBackColorGray4 pBorder1pxSolidGreen pBorderRadius2px lyMt8"
            @click="isReasonEditMode = false"
          >戻って選択する</button>
        </div>
      </div>
    </div>
    <div
      :class="{gbModalBack: true, mdPopUp: true, isShow}"
      @click="emits('request-close')"
    ></div>
  </div>
</template>
